import { memo, type ReactNode } from 'react';

import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

/**
 * The shapes every screen is built from.
 *
 * These lived under `screens/user-detail/` while that was their only caller.
 * They are imported by a dozen screen folders now, and the v2 design uses
 * `Panel` as *the* card on every screen, so they belong here. Real components
 * rather than render helpers, so a tab re-render does not rebuild each panel.
 */

interface PanelProps {
  readonly title: string;
  /** A sentence under the title, for panels whose purpose is not obvious. */
  readonly description?: string;
  /**
   * Who decides what is in this card — "You own this", "From the source, read
   * only". Beside the title rather than in the body: whether an operator may
   * change a number is the first thing they need to know about it, and finding
   * that out by clicking is finding it out too late.
   */
  readonly owner?: ReactNode;
  readonly action?: ReactNode;
  readonly children: ReactNode;
  /**
   * Drops the body padding, for a card that is entirely a table or a list of
   * full-bleed rows. Those supply their own edges and would otherwise sit
   * inside a second, smaller box.
   */
  readonly bleed?: boolean;
  /** A closing strip under the body — a note, a total, a pager. */
  readonly footer?: ReactNode;
  readonly className?: string;
}

/**
 * A `<section>` with an `aria-labelledby`, not a bare div: only a *named*
 * section is exposed as a `region` landmark, which is what lets a screen
 * reader user jump between the panels on a seven-panel screen.
 */
export function Panel({
  title,
  description,
  owner,
  action,
  children,
  bleed = false,
  footer,
  className,
}: PanelProps) {
  const id = `panel-${slug(title)}`;

  return (
    <section
      aria-labelledby={id}
      className={cn('flex flex-col overflow-hidden rounded-xl border border-border bg-card', className)}
    >
      {/*
        No border under the header. A `bleed` card's rows each carry their own
        `border-t`, so the first one already draws the line — adding a second
        here put a doubled rule under every heading in the console.
      */}
      <header className="flex items-center gap-2 px-[18px] pt-3.5 pb-3">
        <div className="flex min-w-0 flex-col gap-1">
          <h3 id={id} className="text-sm leading-none font-semibold text-foreground">
            {title}
          </h3>
          {description === undefined ? null : (
            <p className="text-[13px] leading-relaxed text-pretty text-muted-foreground">{description}</p>
          )}
        </div>
        {owner}
        {action === undefined ? null : <div className="ml-auto shrink-0">{action}</div>}
      </header>
      <div className={cn(!bleed && 'border-t border-border px-[18px] py-3.5')}>{children}</div>
      {footer === undefined ? null : (
        <div className="border-t border-border bg-secondary/40 px-5 py-3">{footer}</div>
      )}
    </section>
  );
}

interface FieldProps {
  readonly label: string;
  readonly value: ReactNode;
  readonly mono?: boolean;
}

/**
 * A `dt`/`dd` pair rather than two divs: a screen reader announcing "Diet,
 * vegetarian" is the whole point of a definition list, and `Fields` supplies
 * the `dl` around it.
 */
export const Field = memo(function Field({ label, value, mono = false }: FieldProps) {
  return (
    <div className="flex flex-col gap-1.5">
      <dt className="text-xs leading-none font-medium text-muted-foreground">{label}</dt>
      <dd className={cn('text-sm text-foreground', mono && 'font-mono text-[13px]')}>
        {/* An em dash reads as "no value"; an empty cell reads as a bug. */}
        {value === null || value === undefined || value === '' ? (
          <span className="text-muted-foreground/60">—</span>
        ) : (
          value
        )}
      </dd>
    </div>
  );
});

export function Fields({ children }: { readonly children: ReactNode }) {
  return <dl className="grid grid-cols-[repeat(auto-fill,minmax(190px,1fr))] gap-x-6 gap-y-5">{children}</dl>;
}

interface FieldRowProps {
  readonly label: string;
  readonly value: ReactNode;
  readonly mono?: boolean;
  /** Ink for a value that is a problem, or is absent on purpose. */
  readonly tone?: 'warn' | 'err' | 'muted';
}

const FIELD_ROW_TONES = {
  warn: 'text-warn-fg',
  err: 'text-err-fg',
  muted: 'text-muted-foreground',
} as const;

/**
 * One labelled fact, as a full-width row.
 *
 * The sibling of `Field`, and the one every record card uses: a stacked
 * label-over-value cell reads well for four numbers side by side and badly for
 * a column of nine, where the eye wants one ruled line per fact and a value
 * edge to run down. Pair with `FieldRows` inside a `bleed` panel — each row
 * draws its own top border, so the card's header line comes free.
 */
export const FieldRow = memo(function FieldRow({ label, value, mono = false, tone }: FieldRowProps) {
  const absent = value === null || value === undefined || value === '';

  return (
    <div className="grid grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] items-baseline gap-3.5 border-t border-border px-[18px] py-2.5">
      <dt className="text-[13px] text-muted-foreground">{label}</dt>
      <dd
        className={cn(
          'text-[13px] font-medium text-pretty text-foreground',
          mono && 'font-mono',
          tone !== undefined && FIELD_ROW_TONES[tone],
        )}
      >
        {/* An em dash reads as "no value"; an empty cell reads as a bug. */}
        {absent ? <span className="font-normal text-muted-foreground/60">—</span> : value}
      </dd>
    </div>
  );
});

export function FieldRows({ children }: { readonly children: ReactNode }) {
  return <dl>{children}</dl>;
}

export interface StatCardData {
  readonly label: string;
  readonly value: ReactNode;
  /** The sentence under the number. Usually what it is a share of. */
  readonly note?: ReactNode;
  /** A tone border, for a card reporting something that needs attention. */
  readonly borderClassName?: string;
}

/**
 * Two columns below `lg` rather than N: four hero figures side by side on a
 * phone are four truncated figures.
 */
const STAT_COLUMNS: Readonly<Record<number, string>> = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-2 lg:grid-cols-3',
  4: 'grid-cols-2 lg:grid-cols-4',
  5: 'grid-cols-2 lg:grid-cols-5',
  6: 'grid-cols-2 lg:grid-cols-6',
};

/**
 * The row of headline figures a screen opens with.
 *
 * Bordered cards rather than a divided strip, because these sit on the page
 * ground with nothing else around them; `StatStrip` is the version that goes
 * inside a `Panel`.
 */
export function StatCards({ stats }: { readonly stats: readonly StatCardData[] }) {
  return (
    <div className={cn('grid gap-3', STAT_COLUMNS[stats.length] ?? 'grid-cols-2 lg:grid-cols-4')}>
      {stats.map((stat) => (
        <div
          key={stat.label}
          className={cn(
            'flex flex-col gap-1.5 rounded-xl border border-border bg-card px-4 pt-3.5 pb-4',
            stat.borderClassName,
          )}
        >
          <span className="text-xs font-medium text-muted-foreground">{stat.label}</span>
          {/* The hero figure. `StatStrip` and `RecordHeader` carry the inline
              one; between them that is the whole of the console's number type. */}
          <span className="font-mono text-2xl leading-none font-medium tracking-[-0.025em] tabular-nums text-foreground">
            {stat.value}
          </span>
          {stat.note === undefined ? null : (
            <span className="text-xs text-pretty text-muted-foreground">{stat.note}</span>
          )}
        </div>
      ))}
    </div>
  );
}

export interface StatStripItem {
  readonly value: ReactNode;
  readonly label: string;
}

/**
 * The divided strip of smaller figures that closes a `Panel` — pass it as the
 * panel's `children` with `bleed`, or as its `footer`. The last cell drops its
 * divider rather than relying on the card's `overflow-hidden` to clip one.
 */
export function StatStrip({ items }: { readonly items: readonly StatStripItem[] }) {
  return (
    <div
      className={cn(
        'grid [&>*:last-child]:border-r-0',
        STAT_COLUMNS[items.length] ?? 'grid-cols-2 lg:grid-cols-3',
      )}
    >
      {items.map((item) => (
        <div key={item.label} className="flex flex-col gap-0.5 border-r border-border px-[18px] pt-3 pb-3.5">
          <span className="font-mono text-base font-medium tabular-nums text-foreground">{item.value}</span>
          <span className="text-xs text-muted-foreground">{item.label}</span>
        </div>
      ))}
    </div>
  );
}

interface EmptyProps {
  readonly title: string;
  readonly body: string;
  /** Typically the action that would fill the emptiness. */
  readonly action?: ReactNode;
}

export function Empty({ title, body, action }: EmptyProps) {
  return (
    <div className="flex flex-col items-center gap-2 px-6 py-12 text-center">
      <p className="text-sm font-semibold text-foreground">{title}</p>
      <p className="max-w-[440px] text-[13px] leading-relaxed text-muted-foreground">{body}</p>
      {action === undefined ? null : <div className="mt-2">{action}</div>}
    </div>
  );
}

/** Skeleton for a tab that is still loading. */
export function TabSkeleton({ rows = 4 }: { readonly rows?: number }) {
  return (
    <div aria-busy="true" aria-live="polite" className="flex flex-col gap-3">
      <span className="sr-only">Loading…</span>
      {Array.from({ length: rows }, (_value, index) => (
        <Skeleton key={index} className="h-16 rounded-xl" />
      ))}
    </div>
  );
}

function slug(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, '-');
}
